/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import { StyleSheet, css } from "aphrodite";
// import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BarChart from "../../../components/HighCharts/BarChart";
import Dashboard from "../../../services/api/dashboard";

const styles = StyleSheet.create({
  card: {
    background: "#fff",
    borderRadius: 16,
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    marginTop: 12,
    width: "100%",
    overflow: "hidden",
    transition: "all 0.3s ease",
    height: 350,
    ":hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
    },
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    gap: 6,
    padding: "12px 16px 8px 16px",
    borderBottom: "1px solid #f0f0f0",
  },
  titleSection: {
    flex: 1,
  },
  title: {
    fontSize: 17,
    fontWeight: 600,
    color: "#1a1a1a",
    margin: 0,
  },
  subtitle: {
    fontSize: 11,
    color: "#666",
    margin: "2px 0 0 0",
    lineHeight: 1.3,
  },
  chartContainer: {
    padding: "8px 12px 12px 12px",
    height: 280,
  },
  totalText: {
    textAlign: "center",
    fontSize: 12,
    color: "#666",
    fontWeight: 500,
    marginTop: 8,
    paddingTop: 8,
    borderTop: "1px solid #f0f0f0",
  },
  loadingContainer: {
    padding: "40px 20px",
    textAlign: "center",
    color: "#666",
    fontSize: 14,
    height: 280,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  errorContainer: {
    padding: "40px 20px",
    textAlign: "center",
    color: "#d32f2f",
    fontSize: 14,
    backgroundColor: "#ffebee",
    margin: "20px",
    borderRadius: 8,
    height: 200,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
  },
});

function NewFriendsTrend() {
  const [chartData, setChartData] = useState({
    months: [],
    friends: [],
    newFriends: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFriends, setShowFriends] = useState(false);
  const [showNewFriends, setShowNewFriends] = useState(true); // default

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await Dashboard.getFriendMonthlyTrend();
        let apiData = response?.data?.data || response?.data || response || [];
        console.log("API RAW:", apiData);

        if (!Array.isArray(apiData)) apiData = [];

        // Filter null months and counts
        const validData = apiData.filter(
          (item) =>
            item.month &&
            (item.friendToMemberCount !== null || item.newFriendCount !== null)
        );

        console.log("VALID:", validData);

        const months = validData.map((item) => {
          const monthName = item.month.split(" ")[0].substring(0, 3);
          const year = item.month.split(" ")[1];
          return `${monthName} ${year}`;
        });

        console.log("MONTHS:", months);

        const friends = validData.map((item) => item.friendToMemberCount || 0);
        const newFriends = validData.map((item) => item.newFriendCount || 0);

        console.log("FRIENDS:", friends);
        console.log("NEW FRIENDS:", newFriends);

        setChartData({ months, friends, newFriends });
      } catch (err) {
        // setError(err.message || "Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const chartOptions = {
    chart: {
      type: "areaspline",
      height: 220,
      backgroundColor: "transparent",
      spacing: [5, 0, 5, 0],
    },
    title: { text: null },
    xAxis: {
      categories: chartData.months,
      lineWidth: 0,
      tickLength: 0,
      labels: { style: { color: "#666", fontSize: "11px" } },
    },
    yAxis: {
      title: {
        text: "Total Number of Friends",
        rotation: -90,
        align: "middle",
        style: { color: "#666", fontSize: "11px" },
      },
      min: 0,
      labels: { style: { color: "#666", fontSize: "11px" } },
      gridLineWidth: 0,
    },
    legend: {
      align: "right",
      verticalAlign: "top",
      layout: "horizontal",
      itemStyle: { color: "#666", fontSize: "11px" },
      symbolWidth: 8,
      symbolHeight: 8,
      symbolRadius: 2,
      itemMarginRight: 12,
      y: -5,
      enabled: false,
    },
    plotOptions: {
      areaspline: {
        fillOpacity: 0.6,
        marker: { enabled: false },
        lineWidth: 2,
      },
    },
    colors: ["#E0E7FF", "#5B63F0"],
    series: [
      showFriends
        ? { id: "friendsSeries", name: "Friends", data: chartData.friends }
        : null,
      showNewFriends
        ? {
            id: "newFriendsSeries",
            name: "New Friends",
            data: chartData.newFriends,
          }
        : null,
    ].filter(Boolean),

    tooltip: {
      shared: true,
      backgroundColor: "rgba(255,255,255,0.95)",
      borderColor: "#ccc",
      borderRadius: 8,
      style: { fontSize: "11px" },
    },
    credits: { enabled: false },
    exporting: { enabled: false },
  };

  if (loading)
    return (
      <div className={css(styles.loadingContainer)}>
        Loading friend trends data...
      </div>
    );
  if (error)
    return <div className={css(styles.errorContainer)}>Error: {error}</div>;
  if (chartData.months.length === 0)
    return (
      <div className={css(styles.loadingContainer)}>No data available</div>
    );

  // const totalFriends = chartData.friends.reduce((a, b) => a + b, 0);
  // const totalNewFriends = chartData.newFriends.reduce((a, b) => a + b, 0);
  const lastValidIndex =
    chartData.friends
      .map((v, i) => ({ v, i }))
      .reverse()
      .find((item) => item.v !== 0)?.i ?? chartData.friends.length - 1;

  const lastFriends = chartData.friends[lastValidIndex] || 0;
  const lastNewFriends = chartData.newFriends[lastValidIndex] || 0;

  return (
    <div className={css(styles.card)}>
      <div className={css(styles.header)}>
        <div className={css(styles.titleSection)}>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <h3 className={css(styles.title)}>New Friends Trend</h3>
            {/* <InfoOutlinedIcon fontSize="small" style={{ color: "#999" }} /> */}
          </div>
          <p className={css(styles.subtitle)}>
            Discussion Meetings (Last 12 month report)
          </p>
        </div>
        {/* <div style={{ display: "flex", gap: 12, padding: "0 16px 8px 16px" }}>
          <label htmlFor="friendsCheck">
            <input
              id="friendsCheck"
              type="checkbox"
              checked={showFriends}
              onChange={() => setShowFriends(!showFriends)}
            />
            Friends
          </label>

          <label htmlFor="newFriendsCheck">
            <input
              id="newFriendsCheck"
              type="checkbox"
              checked={showNewFriends}
              onChange={() => setShowNewFriends(!showNewFriends)}
            />
            New Friends
          </label>
        </div> */}
      </div>
      <div className={css(styles.chartContainer)}>
        <BarChart options={chartOptions} />
        <div className={css(styles.totalText)}>
          {/* Total Friends: {totalFriends.toLocaleString()} | New Friends:{" "}
          {totalNewFriends.toLocaleString()} */}
          Last Month — Friends: {lastFriends.toLocaleString()} | New Friends:{" "}
          {lastNewFriends.toLocaleString()}
        </div>
      </div>
    </div>
  );
}

export default NewFriendsTrend;
